import { useState, useEffect, useRef } from 'react'

// Hands-free speech-to-text on the Web Speech API (webkit-prefixed on
// iOS/Safari — the platform this is built for: replying to the Claude panel
// without touching the phone). One recognition session runs at a time with
// interim results; the browser's own endpointing detects the end of speech,
// at which point the final transcript is delivered via onTranscript and a new
// session starts. TypeScript's DOM lib has no SpeechRecognition typings, so a
// minimal structural type is declared here.

interface RecognitionAlternative { transcript: string }
interface RecognitionResult { isFinal: boolean; 0: RecognitionAlternative }
interface RecognitionResultEvent { resultIndex: number; results: { length: number; [index: number]: RecognitionResult } }
interface RecognitionErrorEvent { error: string }
interface Recognition {
  lang: string
  continuous: boolean
  interimResults: boolean
  onresult: ((e: RecognitionResultEvent) => void) | null
  onerror: ((e: RecognitionErrorEvent) => void) | null
  onend: (() => void) | null
  start(): void
  stop(): void
  abort(): void
}
type RecognitionCtor = new () => Recognition

function getRecognitionCtor(): RecognitionCtor | null {
  if (typeof window === 'undefined') return null
  const w = window as unknown as {
    SpeechRecognition?: RecognitionCtor
    webkitSpeechRecognition?: RecognitionCtor
  }
  return w.SpeechRecognition || w.webkitSpeechRecognition || null
}

export function voiceRecognitionSupported(): boolean {
  return getRecognitionCtor() !== null
}

/**
 * Keep speech recognition running while `active` is true.
 *
 * - Interim transcript (what's being said right now) streams via onInterim.
 * - When the browser detects end of speech, the FINAL transcript goes to
 *   onTranscript (the caller accumulates segments — see useVoiceSendBuffer —
 *   rather than sending each one) and listening restarts.
 * - Silence with no speech at all just restarts the session — the mic stays
 *   hot until `active` goes false (the caller flips it off while the agent is
 *   working or a reply is being read aloud, preventing the TTS feedback loop).
 * - A denied mic permission surfaces in `micError` and stops the loop — but
 *   ONLY when the page is visible. iOS denies the microphone to web pages
 *   whenever the page is hidden (screen locked, app backgrounded); that is a
 *   transient platform limit, not a revoked permission, so the loop PARKS
 *   (`suspended` true, no error) and revives itself the moment the page is
 *   visible again.
 */
export function useVoiceRecognition({
  active,
  onTranscript,
  onInterim,
}: {
  active: boolean
  onTranscript: (text: string) => void
  onInterim?: (text: string) => void
}): { listening: boolean; micError: string | null; suspended: boolean } {
  const [listening, setListening] = useState(false)
  const [micError, setMicError] = useState<string | null>(null)
  const [suspended, setSuspended] = useState(false)

  // Callbacks live in refs so the session effect only depends on `active` —
  // a re-created callback must not abort and restart a live mic session.
  const onTranscriptRef = useRef(onTranscript)
  const onInterimRef = useRef(onInterim)
  onTranscriptRef.current = onTranscript
  onInterimRef.current = onInterim

  useEffect(() => {
    if (!active) return
    const Ctor = getRecognitionCtor()
    if (!Ctor) {
      setMicError('not supported in this browser')
      return
    }
    setMicError(null)
    setSuspended(false)

    let disposed = false
    let deniedForever = false
    let sessionLive = false
    let recognition: Recognition | null = null
    let restartTimer: ReturnType<typeof setTimeout> | null = null

    const pageHidden = () =>
      typeof document !== 'undefined' && document.visibilityState === 'hidden'

    const startSession = () => {
      if (disposed || deniedForever || sessionLive) return
      // Hidden page (screen locked / backgrounded): iOS will answer any
      // recognition attempt with not-allowed. Park; the visibility listener
      // revives the loop on unlock.
      if (pageHidden()) {
        setSuspended(true)
        return
      }
      const rec = new Ctor()
      recognition = rec
      rec.lang = typeof navigator !== 'undefined' && navigator.language ? navigator.language : 'en-US'
      rec.continuous = false
      rec.interimResults = true

      let finalText = ''

      rec.onresult = (e) => {
        let interim = ''
        finalText = ''
        for (let i = 0; i < e.results.length; i++) {
          const r = e.results[i]
          if (r.isFinal) finalText += r[0].transcript
          else interim += r[0].transcript
        }
        onInterimRef.current?.((finalText + interim).trim())
      }
      rec.onerror = (e) => {
        // 'no-speech' / 'aborted' are normal session ends — onend restarts.
        if (e.error === 'not-allowed' || e.error === 'service-not-allowed') {
          // The same error means two different things: a REAL permission
          // denial (page visible) or the transient hidden-page suspension.
          // Only the former is terminal; onend's restart parks the latter
          // via the hidden-guard above.
          if (pageHidden()) return
          deniedForever = true
          setMicError('microphone access was denied')
        }
      }
      rec.onend = () => {
        sessionLive = false
        if (disposed) return
        setListening(false)
        onInterimRef.current?.('')
        const text = finalText.trim()
        if (text) onTranscriptRef.current(text)
        if (deniedForever) return
        // Keep the mic hot: new session after a beat (also avoids a tight
        // restart loop when the engine ends immediately).
        restartTimer = setTimeout(startSession, 300)
      }

      try {
        rec.start()
        sessionLive = true
        setListening(true)
        setSuspended(false)
      } catch {
        // start() throws if a session is somehow still active — retry shortly.
        restartTimer = setTimeout(startSession, 500)
      }
    }

    // Revive a lock-parked loop the moment the page is visible again.
    const onVisibility = () => {
      if (typeof document === 'undefined' || document.visibilityState !== 'visible') return
      if (restartTimer) clearTimeout(restartTimer)
      startSession()
    }
    if (typeof document !== 'undefined') {
      document.addEventListener('visibilitychange', onVisibility)
    }

    startSession()

    return () => {
      disposed = true
      if (restartTimer) clearTimeout(restartTimer)
      if (typeof document !== 'undefined') {
        document.removeEventListener('visibilitychange', onVisibility)
      }
      try { recognition?.abort() } catch { /* already stopped */ }
      setListening(false)
      setSuspended(false)
      onInterimRef.current?.('')
    }
  }, [active])

  return { listening, micError, suspended }
}
